import { CarFleetViewRecord } from '@navio/nsdk';
import { type OrderingKeyOfUrlKey, URLKeys } from 'noddi-async';
import { BadgeVariant, NoddiBadge, NoddiColumnDef } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { CarInfoCell } from '../CarDetails/Cards/CarInfoCell';
import { CarListVehicleCell } from '../CarDetails/Cards/CarListVehicleCell';
import { NavioTireSeasonChip } from '../CarWheel/NavioTireSeasonChip';
import type { CarGroup } from '../Notifications/notificationGroupTypes';
import { BadgeColumnContainer } from './BadgeColumnContainer';
import { fleetTreadDepthStatus, FleetTreadDepthStatus, TIRE_SEASON, TireSeason } from './constants';
import { DriversCell } from './DriversCell';
import { driverSearchText, getDriversForCars } from './driversForCars';
import { FleetScheduleCell } from './FleetScheduleCell';
import { FleetViewOwnersCell } from './FleetViewOwnersCell';
import { defaultFleetViewColumnsTranslations, FleetViewColumnsTranslations } from './fleetViewTranslations';
import { LatestCompletedBookingCell } from './LatestCompletedBookingCell';
import { UpcomingBookingCell } from './UpcomingBookingCell';

const getTireWearBadgeVariant = (status: FleetTreadDepthStatus): BadgeVariant => {
  switch (status) {
    case fleetTreadDepthStatus.WORN_OUT:
      return 'destructive';
    case fleetTreadDepthStatus.SOON_WORN_OUT:
      return 'warning';
    case fleetTreadDepthStatus.ABOVE_RECOMMENDED:
      return 'success';
    case fleetTreadDepthStatus.NO_MEASUREMENTS:
      return 'outline';
    default:
      return 'outline';
  }
};

const getTreadDepthLabel = (status: FleetTreadDepthStatus, translations: FleetViewColumnsTranslations): string => {
  switch (status) {
    case fleetTreadDepthStatus.WORN_OUT:
      return translations.mustBeReplaced;
    case fleetTreadDepthStatus.SOON_WORN_OUT:
      return translations.shouldBeReplaced;
    case fleetTreadDepthStatus.ABOVE_RECOMMENDED:
      return translations.goodCondition;
    case fleetTreadDepthStatus.NO_MEASUREMENTS:
      return translations.noMeasurements;
    default:
      return status;
  }
};

const getTireWearDataBySeason = (tireWear: CarFleetViewRecord['tireWear'], season: TireSeason) => {
  if (!tireWear?.wheelSets?.length) {
    return null;
  }

  const wheelSet = tireWear.wheelSets.find((ws) => ws.tireSeason.value === season);
  if (!wheelSet?.status) {
    return null;
  }
  return { status: wheelSet.status, lastMeasuredAt: wheelSet.lastMeasuredAt };
};

type FleetViewOrderingKey = OrderingKeyOfUrlKey<typeof URLKeys.getPaginatedFleetViewCars>;

export const getFleetViewColumns = (
  translations?: FleetViewColumnsTranslations,
  options?: {
    showTireHotelColumns?: boolean;
    showUserGroupColumn?: boolean;
    showDriverColumn?: boolean;
    showFleetScheduleColumn?: boolean;
    /** Two-line catalog identity. Off so other fleet tables keep the single name line. */
    catalogVehicle?: boolean;
    carManagers?: CarGroup[];
  }
): NoddiColumnDef<CarFleetViewRecord, unknown, FleetViewOrderingKey>[] => {
  const t = translations ?? defaultFleetViewColumnsTranslations;

  const tireHotelColumns: NoddiColumnDef<CarFleetViewRecord, unknown, FleetViewOrderingKey>[] = [
    {
      id: 'tireHotel',
      header: t.wheelSetAtTireHotel,
      cell: ({ row }) => {
        const firstWheelStorage = row.original.wheelStorage[0];
        if (!firstWheelStorage?.carWheelSet) {
          return (
            <BadgeColumnContainer>
              <NoddiBadge variant='outline'>{t.none}</NoddiBadge>
            </BadgeColumnContainer>
          );
        }
        const season = firstWheelStorage.carWheelSet.tireSeason;
        return (
          <BadgeColumnContainer>
            <NavioTireSeasonChip
              season={season.value}
              size='sm'
              translations={{
                summerTires: t.summerTires,
                winterTires: t.winterTires,
                allSeasonTires: t.allSeasonTires
              }}
            />
          </BadgeColumnContainer>
        );
      }
    },
    {
      id: 'summerTireCondition',
      accessorKey: 'minTreadDepthSummer',
      header: t.summerTires,
      cell: ({ row }) => {
        const data = getTireWearDataBySeason(row.original.tireWear, TIRE_SEASON.SUMMER);
        if (!data) {
          return (
            <BadgeColumnContainer>
              <NoddiBadge variant='outline'>{t.noMeasurements}</NoddiBadge>
            </BadgeColumnContainer>
          );
        }
        return (
          <BadgeColumnContainer>
            <div className='flex flex-col gap-0.5'>
              <NoddiBadge variant={getTireWearBadgeVariant(data.status)}>
                {getTreadDepthLabel(data.status, t)}
              </NoddiBadge>
              {data.lastMeasuredAt && (
                <span className='text-xs text-muted-foreground'>
                  {noddiFormatDate(data.lastMeasuredAt, NoddiDateFormats.DATE)}
                </span>
              )}
            </div>
          </BadgeColumnContainer>
        );
      },
      meta: {
        orderingKey: 'min_tread_depth_summer'
      }
    },
    {
      id: 'winterTireCondition',
      accessorKey: 'minTreadDepthWinter',
      header: t.winterTires,
      cell: ({ row }) => {
        const data = getTireWearDataBySeason(row.original.tireWear, TIRE_SEASON.WINTER);
        if (!data) {
          return (
            <BadgeColumnContainer>
              <NoddiBadge variant='outline'>{t.noMeasurements}</NoddiBadge>
            </BadgeColumnContainer>
          );
        }
        return (
          <BadgeColumnContainer>
            <div className='flex flex-col gap-0.5'>
              <NoddiBadge variant={getTireWearBadgeVariant(data.status)}>
                {getTreadDepthLabel(data.status, t)}
              </NoddiBadge>
              {data.lastMeasuredAt && (
                <span className='text-xs text-muted-foreground'>
                  {noddiFormatDate(data.lastMeasuredAt, NoddiDateFormats.DATE)}
                </span>
              )}
            </div>
          </BadgeColumnContainer>
        );
      },
      meta: {
        orderingKey: 'min_tread_depth_winter'
      }
    }
  ];

  const userGroupColumn: NoddiColumnDef<CarFleetViewRecord, unknown, FleetViewOrderingKey> = {
    id: 'userGroup',
    header: t.customer,
    accessorFn: (row) => {
      const owners = row.ownersCurrent ?? [];
      if (owners.length > 0) {
        return owners.map((owner) => owner.userGroup.name).join(', ');
      }
      return row.userGroup?.name ?? '';
    },
    cell: ({ row }) => (
      <FleetViewOwnersCell
        owners={row.original.ownersCurrent}
        fallbackUserGroup={row.original.userGroup}
        translations={t}
      />
    )
  };

  const driverColumn: NoddiColumnDef<CarFleetViewRecord, unknown, FleetViewOrderingKey> = {
    id: 'driver',
    header: t.driver,
    accessorFn: (row) => driverSearchText(getDriversForCars([row.id], options?.carManagers)),
    cell: ({ row }) => <DriversCell drivers={getDriversForCars([row.original.id], options?.carManagers)} />
  };

  const fleetScheduleColumn: NoddiColumnDef<CarFleetViewRecord, unknown, FleetViewOrderingKey> = {
    id: 'fleetSchedule',
    header: t.fleetSchedule,
    accessorFn: (row) => row.fleetSchedule?.fleetScheduleName ?? '',
    cell: ({ row }) => <FleetScheduleCell fleetSchedule={row.original.fleetSchedule} noneLabel={t.none} />
  };

  return [
    {
      id: 'car',
      header: t.car,
      cell: ({ row }) =>
        options?.catalogVehicle ? (
          <CarListVehicleCell car={row.original} showLicensePlate />
        ) : (
          <CarInfoCell car={row.original} />
        )
    },
    ...(options?.showDriverColumn ? [driverColumn] : []),
    ...(options?.showUserGroupColumn ? [userGroupColumn] : []),
    ...(options?.showFleetScheduleColumn ? [fleetScheduleColumn] : []),
    ...(options?.showTireHotelColumns ? tireHotelColumns : []),
    {
      id: 'lastBookingDate',
      accessorKey: 'lastBookingDate',
      header: t.lastBooking,
      cell: ({ row }) => (
        <LatestCompletedBookingCell
          booking={row.original.latestCompletedBooking}
          cancelledLabel={t.cancelled}
          noneLabel={t.none}
        />
      ),
      meta: {
        orderingKey: 'last_booking_date'
      }
    },
    {
      id: 'upcomingBookingDate',
      accessorKey: 'upcomingBookingDate',
      header: t.upcomingBooking,
      cell: ({ row }) => (
        <UpcomingBookingCell booking={row.original.upcomingBooking} cancelledLabel={t.cancelled} noneLabel={t.none} />
      ),
      meta: {
        orderingKey: 'upcoming_booking_date'
      }
    }
  ];
};
